import { CURRENT_KEY_ID, SESSION_KEY_LOCK, KEY_DB_NAME, KEY_STORE_NAME, UNSUPPORTED_BROWSER_MESSAGE } from '~constants/session';

import { publishSessionMessage } from '~libs/sessionSync';

// The browser-bound session key. A non-extractable ECDSA P-256 key pair lives in IndexedDB, so page script
// (and anything injected into it) can sign with it but can never read the private key. The API stores the
// public half against the session and demands a fresh signature on every request.

export interface KeyPair {
  publicKey: CryptoKey;
  privateKey: CryptoKey;
}

export interface StoredKey {
  keyPair: KeyPair;
  createdAt: number;
}

export interface KeyStore {
  get(id: string): Promise<StoredKey | undefined>;
  put(id: string, value: StoredKey): Promise<void>;
  delete(id: string): Promise<void>;
  clear(): Promise<void>;
}

export interface PublicJwk {
  kty: string;
  crv: string;
  x: string;
  y: string;
}

export class ProofUnsupportedError extends Error {
  constructor(message: string = UNSUPPORTED_BROWSER_MESSAGE) {
    super(message);
    this.name = 'ProofUnsupportedError';
  }
}

const KEY_ALGORITHM = { name: 'ECDSA', namedCurve: 'P-256' } as const;
const PENDING_PREFIX = 'pending:';
const PROBE_PREFIX = 'probe:';

const openDatabase = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(KEY_DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(KEY_STORE_NAME)) {
        request.result.createObjectStore(KEY_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('IndexedDB is blocked'));
  });

const runTransaction = async <T>(
  mode: 'readonly' | 'readwrite',
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const database = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    let request: IDBRequest<T>;
    try {
      const transaction = database.transaction(KEY_STORE_NAME, mode);
      request = run(transaction.objectStore(KEY_STORE_NAME));
      transaction.oncomplete = () => {
        database.close();
        resolve(request.result);
      };
      transaction.onerror = () => {
        database.close();
        reject(transaction.error);
      };
      transaction.onabort = () => {
        database.close();
        reject(transaction.error);
      };
    } catch (error) {
      database.close();
      reject(error);
    }
  });
};

const createIndexedDbStore = (): KeyStore => ({
  get: (id) => runTransaction('readonly', (store) => store.get(id) as IDBRequest<StoredKey | undefined>),
  put: async (id, value) => {
    await runTransaction('readwrite', (store) => store.put(value, id));
  },
  delete: async (id) => {
    await runTransaction('readwrite', (store) => store.delete(id));
  },
  clear: async () => {
    await runTransaction('readwrite', (store) => store.clear());
  },
});

let keyStore: KeyStore | null = null;
// undefined: not loaded yet. null: loaded, and there is no key.
let cachedKeyPair: KeyPair | null | undefined;
let loadingKeyPair: Promise<KeyPair | null> | null = null;
let supportCheck: Promise<boolean> | null = null;

const getStore = (): KeyStore => {
  if (!keyStore) keyStore = createIndexedDbStore();
  return keyStore;
};

/** Test seam: swaps the store and forgets everything memoised. */
export const __setKeyStoreForTests = (store: KeyStore | null) => {
  keyStore = store;
  cachedKeyPair = undefined;
  loadingKeyPair = null;
  supportCheck = null;
};

export const resetKeyCache = () => {
  cachedKeyPair = undefined;
  loadingKeyPair = null;
};

const generateKeyPair = (): Promise<KeyPair> =>
  crypto.subtle.generateKey(KEY_ALGORITHM, false, ['sign', 'verify']) as Promise<KeyPair>;

const exportPublicJwk = async (publicKey: CryptoKey): Promise<PublicJwk> => {
  const { kty, crv, x, y } = await crypto.subtle.exportKey('jwk', publicKey);
  if (!kty || !crv || !x || !y) throw new ProofUnsupportedError();
  return { kty, crv, x, y };
};

const probe = async (): Promise<boolean> => {
  if (typeof window === 'undefined' || !window.isSecureContext) return false;
  if (typeof crypto === 'undefined' || !crypto.subtle) return false;
  if (typeof indexedDB === 'undefined' && !keyStore) return false;

  // A real round-trip: private browsing modes and blocked-storage settings expose the APIs but fail on use.
  const id = `${PROBE_PREFIX}${crypto.randomUUID()}`;
  const store = getStore();
  const keyPair = await generateKeyPair();
  await store.put(id, { keyPair, createdAt: Date.now() });
  const stored = await store.get(id);
  await store.delete(id);
  return Boolean(stored?.keyPair);
};

/** Whether this browser can hold a non-extractable session key. Memoised for the page's lifetime. */
export const checkProofSupport = (): Promise<boolean> => {
  if (!supportCheck) {
    supportCheck = probe().catch(() => false);
  }
  return supportCheck;
};

/**
 * Creates a key pair for a sign-in that has not succeeded yet. The private key never leaves the browser; only
 * the public JWK is returned, for the request body.
 */
export const createPendingKey = async (): Promise<{ pendingId: string; publicJwk: PublicJwk }> => {
  try {
    if (!(await checkProofSupport())) throw new ProofUnsupportedError();
    const keyPair = await generateKeyPair();
    const pendingId = `${PENDING_PREFIX}${crypto.randomUUID()}`;
    await getStore().put(pendingId, { keyPair, createdAt: Date.now() });
    return { pendingId, publicJwk: await exportPublicJwk(keyPair.publicKey) };
  } catch (error) {
    throw error instanceof ProofUnsupportedError ? error : new ProofUnsupportedError();
  }
};

/** The sign-in succeeded: the pending key becomes the key every tab signs with. */
export const commitPendingKey = async (pendingId: string): Promise<void> => {
  const store = getStore();
  const record = await store.get(pendingId);
  if (!record) throw new ProofUnsupportedError();
  await store.put(CURRENT_KEY_ID, record);
  await store.delete(pendingId);
  cachedKeyPair = record.keyPair;
  loadingKeyPair = null;
  publishSessionMessage({ type: 'key-changed' });
};

/** The sign-in failed: nothing was registered with the API, so the key is thrown away. */
export const discardPendingKey = async (pendingId: string): Promise<void> => {
  try {
    await getStore().delete(pendingId);
  } catch {
    // Best effort. An orphaned pending record is inert.
  }
};

export const hasKeyLocks = (): boolean => typeof navigator !== 'undefined' && Boolean(navigator.locks);

/**
 * Takes the cross-tab key lock and returns its release. Session establishment holds it from the pending key
 * to the commit or discard, so two tabs cannot interleave and leave the key and the cookie from different
 * sign-ins. Without Web Locks this resolves at once with a no-op release.
 */
export const acquireKeyLock = (): Promise<() => void> => {
  if (!hasKeyLocks()) return Promise.resolve(() => undefined);
  return new Promise<() => void>((resolveAcquired) => {
    navigator.locks
      .request(SESSION_KEY_LOCK, () => new Promise<void>((release) => resolveAcquired(() => release())))
      .catch(() => resolveAcquired(() => undefined));
  });
};

/** Waits for any establishment in progress (in any tab) to finish, so the key read next is the committed one. */
export const waitForKeyCommit = async (): Promise<void> => {
  const release = await acquireKeyLock();
  release();
};

/** The key every request is signed with, or null when this browser has none. */
export const getCurrentKeyPair = (): Promise<KeyPair | null> => {
  if (cachedKeyPair !== undefined) return Promise.resolve(cachedKeyPair);
  if (!loadingKeyPair) {
    loadingKeyPair = getStore()
      .get(CURRENT_KEY_ID)
      .then((record) => {
        cachedKeyPair = record?.keyPair ?? null;
        return cachedKeyPair;
      })
      .catch(() => null)
      .finally(() => {
        loadingKeyPair = null;
      });
  }
  return loadingKeyPair;
};

/**
 * Explicit sign-out only. An expiring stale tab must not delete a key another tab has just created. Runs under
 * the key lock, so a sign-in in progress in another tab finishes its commit first instead of losing its pending
 * key mid-way. While the lock is held no tab has a pending key, so clearing the whole store is safe.
 */
export const clearKeys = async (): Promise<void> => {
  const release = await acquireKeyLock();
  try {
    await getStore().clear();
  } catch {
    // Nothing to clear, or storage is unavailable. Either way the cache below is what signing reads.
  } finally {
    release();
  }
  cachedKeyPair = null;
  loadingKeyPair = null;
};
